import React, { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import "./Hero.css";
import heroVideo from "../../assets/hero.mp4";

const Hero = ({ animationState }) => {
  const videoRef = useRef(null);
  const hasStartedRef = useRef(false);
  const timeoutRef = useRef(null);
  const location = useLocation();
  const isLandingPage = location.pathname === "/";

  // Preload video immediately (even before first animation starts)
  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;

    // Set up video for preloading (but don't play yet)
    // The video element's preload="auto" handles loading without a separate link preload
    videoEl.preload = "auto";
    videoEl.muted = true;
    videoEl.playsInline = true;
    videoEl.webkitPlaysInline = true;
    videoEl.disableRemotePlayback = true;
    videoEl.controls = false;
    videoEl.controlsList = "nodownload noremoteplayback";
    videoEl.defaultPlaybackRate = 1;
    videoEl.playbackRate = 1;
    videoEl.loop = true; // Ensure loop is set programmatically
    
    // Ensure video loops infinitely
    const handleEnded = () => {
      videoEl.currentTime = 0;
      videoEl.play().catch(() => {});
    };
    
    videoEl.addEventListener("ended", handleEnded, { passive: true });
    
    // Start loading the video immediately for buffering
    videoEl.load();

    return () => {
      videoEl.removeEventListener("ended", handleEnded);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  // Track previous location to detect when returning to landing page
  const prevLocationRef = useRef(location.pathname);

  // Helper function to start video from the beginning
  const startVideoFromBeginning = useCallback(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;

    const startVideo = () => {
      // Ensure video is at the start
      videoEl.currentTime = 0;
      
      // Play the video
      const playPromise = videoEl.play();
      if (playPromise && playPromise.catch) {
        playPromise.catch((error) => {
          console.log("Video play error:", error);
          // Retry once if needed (reduced delay)
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          timeoutRef.current = setTimeout(() => {
            videoEl.currentTime = 0;
            videoEl.play().catch(() => {});
            timeoutRef.current = null;
          }, 50);
        });
      }
      hasStartedRef.current = true;
    };

    // Try to start immediately
    if (videoEl.readyState >= 2) { // HAVE_CURRENT_DATA or better
      videoEl.currentTime = 0;
      startVideo();
    } else {
      // Wait for video to have some data, then start
      const handleReady = () => {
        if (!hasStartedRef.current) {
          videoEl.currentTime = 0;
          startVideo();
        }
      };
      
      // Try multiple events to catch when video is ready
      videoEl.addEventListener("canplay", handleReady, { once: true });
      videoEl.addEventListener("canplaythrough", handleReady, { once: true });
      videoEl.addEventListener("loadeddata", handleReady, { once: true });
      
      // Fallback: try after a short delay (reduced from 500ms)
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        if (!hasStartedRef.current && videoEl.readyState >= 1) {
          videoEl.currentTime = 0;
          startVideo();
        }
        timeoutRef.current = null;
      }, 200);
    }
  }, []);

  // Handle navigation: pause/reset when leaving, restart when returning
  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;

    const wasOnLandingPage = prevLocationRef.current === "/";
    const isNowOnLandingPage = isLandingPage;

    // If navigating away from landing page, pause and reset
    if (wasOnLandingPage && !isNowOnLandingPage) {
      videoEl.pause();
      videoEl.currentTime = 0;
      hasStartedRef.current = false;
    }

    // If returning to landing page, reset and restart
    if (!wasOnLandingPage && isNowOnLandingPage) {
      hasStartedRef.current = false;
      videoEl.pause();
      videoEl.currentTime = 0;
      
      // Restart video if animation state is ready (reduced delay)
      if (animationState === "finalText" || animationState === "navbarVisible") {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
          startVideoFromBeginning();
          timeoutRef.current = null;
        }, 50);
      }
    }

    // Update previous location
    prevLocationRef.current = location.pathname;
  }, [isLandingPage, location.pathname, animationState, startVideoFromBeginning]);

  // Start video when finalText appears - exactly at the beginning
  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl || !isLandingPage) return;

    if ((animationState === "finalText" || animationState === "navbarVisible") && !hasStartedRef.current) {
      startVideoFromBeginning();
    }
  }, [animationState, isLandingPage, startVideoFromBeginning]);

  // Handle window resize to prevent video from turning black on small screens
  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;

    const handleResize = () => {
      // Force video to reload its display on resize
      if (videoEl.readyState >= 2 && hasStartedRef.current) {
        // Trigger a reflow to ensure video renders properly
        videoEl.style.display = 'none';
        void videoEl.offsetHeight; // Force reflow
        videoEl.style.display = 'block';
        
        // Ensure video is still playing after resize
        if (videoEl.paused) {
          videoEl.play().catch(() => {});
        }
      }
    };

    // Use debounce to avoid too many resize events
    let resizeTimeout;
    const debouncedResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleResize, 150);
    };

    window.addEventListener('resize', debouncedResize, { passive: true });
    window.addEventListener('orientationchange', handleResize, { passive: true });

    return () => {
      window.removeEventListener('resize', debouncedResize);
      window.removeEventListener('orientationchange', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, []);

  return (
    <section className="hero-experience">
      <div className="hero-video-stage">
        <video
          ref={videoRef}
          className="hero-video"
          src={heroVideo}
          playsInline
          muted
          loop
          preload="auto"
          crossOrigin="anonymous"
          disablePictureInPicture
          controlsList="nodownload noremoteplayback"
          fetchpriority="high"
          webkit-playsinline="true"
          x5-playsinline="true"
          x5-video-player-type="h5"
          x5-video-player-fullscreen="true"
          x5-video-orientation="portrait"
        />
      </div>
    </section>
  );
};

export default Hero;
